// @flow
import React, { Fragment } from "react";
import classNames from "classnames";
import startCase from "lodash/startCase";
import withStyles from "elevate-ui/withStyles";
import Typography from "elevate-ui/Typography";
import Paper from "elevate-ui/Paper";
import Modal from "elevate-ui/Modal";
import { withSellerLeadContext } from "../common/SellerLeadContext";
import { withGlobalContext } from "../common/GlobalContext";
import HighBellGraph from "../common/icons/HighBellGraph";
import MidBellGraph from "../common/icons/MidBellGraph";
import LowBellGraph from "../common/icons/LowBellGraph";
import Button from "elevate-ui/Button";
import HelpOutline from "elevate-ui-icons/HelpOutline";
import type { $AttomData } from "server/web/controllers/seller-lead-ctrl";

type $Props = {
  classes: Object,
  avm: $AttomData,
  address: Object,
  emailAgent: Function,
  renderIcon: Function,
};

type $AVMData = {
  avm: Object,
  classes: Object,
  renderIcon: Function,
};

type $StateGraph = {
  showAVMModal: boolean,
};

class SellerPageStats extends React.Component<$AVMData> {
  props: $AVMData;

  render() {
    const { classes, avm, renderIcon } = this.props;
    return (
      <div className={classes.propertyStatsWrappers}>
        {avm.properyStats.map((stat) => {
          const key = Object.keys(stat)[0];
          return stat[key] ? (
            <div key={key} className={classes.propertyStatsWrapper}>
              <div>{renderIcon(stat.icon)}</div>
              <div
                className={classNames(
                  classes.statValue,
                  classes.statValueMargin
                )}
              >
                {stat[key]}
              </div>
              <div
                className={classNames(
                  classes.statLabel,
                  classes.statLabelMargin
                )}
              >
                {startCase(key)}
              </div>
            </div>
          ) : null;
        })}
      </div>
    );
  }
}

class SellerPageValuationGraph extends React.Component<$AVMData, $StateGraph> {
  constructor(props: $AVMData) {
    super(props);

    this.state = {
      showAVMModal: false,
    };
  }

  handleAVMModalToggle = () => {
    this.setState({ showAVMModal: !this.state.showAVMModal });
  };

  render() {
    const { avm, classes, renderIcon } = this.props;
    const { showAVMModal } = this.state;
    return (
      <Fragment>
        <Paper
          onClick={() => this.handleAVMModalToggle()}
          className={classes.valuationGraphWrapper}
        >
          <Typography
            type="body"
            className={classNames(classes.textContent, classes.subTitle)}
          >
            Estimated Value
            <div className={classes.iconContainer}>
              <HelpOutline />
            </div>
          </Typography>
          <div className={classes.estimatesWrapper}>
            <div className={classes.estimateWrapper}>
              <div>
                <LowBellGraph />
              </div>
              <div className={classes.statValue}>
                {avm.automatedValuationModel.lowFormatted}
              </div>
              <div
                className={classNames(
                  classes.statLabel,
                  classes.statLabelMargin
                )}
              >
                Low Estimate
              </div>
            </div>
            <div className={classes.estimateWrapper}>
              <div>
                <MidBellGraph />
              </div>
              <div className={classes.statValue}>
                {avm.automatedValuationModel.midFormatted}
              </div>
              <div
                className={classNames(
                  classes.statLabel,
                  classes.statLabelMargin
                )}
              >
                Mid Estimate
              </div>
            </div>
            <div className={classes.estimateWrapper}>
              <div>
                <HighBellGraph />
              </div>
              <div className={classes.statValue}>
                {avm.automatedValuationModel.highFormatted}
              </div>
              <div
                className={classNames(
                  classes.statLabel,
                  classes.statLabelMargin
                )}
              >
                High Estimate
              </div>
            </div>
            <div className={classes.estimateWrapper}>
              <div className={classes.stack}>
                <div className={classes.confidenceScore}>
                  {renderIcon(
                    avm.automatedValuationModel.confidenceScore > 80
                      ? "High"
                      : avm.automatedValuationModel.confidenceScore < 60
                        ? "Low"
                        : "Mid"
                  )}
                </div>
                <div
                  className={classNames(
                    classes.statValue,
                    classes.confidenceScore,
                    classes.confidenceScoreValue
                  )}
                >
                  {avm.automatedValuationModel.confidenceScore}
                </div>
              </div>
              <div
                className={classNames(
                  classes.statLabel,
                  classes.confidenceLable
                )}
              >
                Confidence Score
              </div>
            </div>
          </div>
        </Paper>
        <Modal
          showFooter={false}
          title="Automated Valuation Model"
          visible={showAVMModal}
          toggleModal={() => this.handleAVMModalToggle()}
        >
          <Typography type="body" gutterBottom>
            Our AVM (automated valuation model) uses many different algorithm
            models to analyze a home’s value. We then put each model’s result
            through an accuracy analysis and use the highest scoring one to
            display a range of value. Our confidence score then is a measure
            between 0 and 100 of how confident we are that we’ve estimated the
            value of the home within a percentage of the current market value.
            So a confidence score of 92 means we are very confident that our
            estimated sale price range is in-line with the true market value.
            The lower the score, the less confident we are in the prediction.
            Lower confidence scores are usually due to the uniqueness of the
            home or the limited availability of data.
          </Typography>
          <Typography type="body">
            Please keep in mind this estimated value is not an appraisal. Use it
            as a starting point to determine value. For the most accurate value,
            please consider speaking with one of our local experts. They will
            take into account the most recent specific local market conditions
            &amp; the specific features your home has. They’ll then provide you
            with a much more precise valuation than any auto generated computer
            estimate.
          </Typography>
        </Modal>
      </Fragment>
    );
  }
}

class SellerPageThankYou extends React.Component<$Props> {
  props: $Props;

  componentDidMount() {
    window.scrollTo(0, 0);
  }

  renderBlueBox(title: string) {
    const { classes, emailAgent } = this.props;
    return (
      <div className={classNames(classes.box1Color, classes.contactBox)}>
        <div className={classes.textFlex}>
          <Typography
            type="body"
            className={classNames(classes.textContent, classes.contactTitle)}
          >
            {title}
          </Typography>
          <Typography
            type="body"
            className={classNames(classes.textContent, classes.contactBody)}
          >
            Get an accurate home value report
          </Typography>
          <Typography
            type="body"
            className={classNames(classes.textContent, classes.contactBody)}
          >
            from a local pro here. (It's free)
          </Typography>
        </div>

        <div className={classNames(classes.textFlex, classes.contactButton)}>
          <Button
            color="#fff"
            className={classNames(classes.boxButton)}
            type="button"
            onClick={() => emailAgent("askAProfessional")}
          >
            <span className={classNames(classes.boxButtonText)}>
              Ask a Professional!
            </span>
          </Button>
          <Typography
            type="body"
            className={classNames(classes.contactSubText)}
          >
            1000's of accurate valuations given!
          </Typography>
        </div>
      </div>
    );
  }

  renderGreenBox() {
    const { classes, emailAgent } = this.props;
    return (
      <div className={classNames(classes.box2Color, classes.contactBox)}>
        <div className={classes.textFlex}>
          <Typography
            type="body"
            className={classNames(classes.textContent, classes.contactTitle)}
          >
            Thinking About Selling?
          </Typography>
          <Typography
            type="body"
            className={classNames(classes.textContent, classes.contactBody)}
          >
            Investigate your local market and smartest options with a
            no-obligation consultation from a local professional.
          </Typography>
        </div>

        <div className={classNames(classes.textFlex, classes.contactButton)}>
          <Button
            color="#fff"
            className={classNames(
              classes.boxButton,
              classes.boxBottomNoSubText
            )}
            type="button"
            onClick={() => emailAgent("requestAFreeConsultation")}
          >
            <span className={classes.boxButtonText}>
              Request a Free Consultation
            </span>
          </Button>
        </div>
      </div>
    );
  }

  renderTitle(avmType: string) {
    const { classes } = this.props;

    let title =
      "Oops! Looks like your home is in a unique class of properties.";
    if (avmType === "lux") {
      title =
        "Congratulations! Your home is in a special class of Luxury Homes.";
    } else if (avmType === "found" || avmType === "foundWithValuation") {
      title = "Alright! Here's Your Automatic Valuation For:";
    }

    return (
      <Typography
        type="body"
        className={classNames(
          classes.textContent,
          classes.subTitle,
          classes.marginBottom
        )}
      >
        {title}
      </Typography>
    );
  }

  render() {
    const { classes, avm, address, renderIcon } = this.props;

    let avmType = "missing";
    if (
      avm.automatedValuationModel &&
      avm.automatedValuationModel.overMillion
    ) {
      avmType = "lux";
    } else if (
      avm.automatedValuationModel &&
      avm.properyStats.length > 0 &&
      avm.automatedValuationModel.midFormatted
    ) {
      avmType = "foundWithValuation";
    } else if (avm.automatedValuationModel && avm.properyStats.length > 0) {
      avmType = "found";
    }

    return (
      <div className={classNames(classes.boxWrapper)}>
        <div className={classes.box}>
          {this.renderTitle(avmType)}
          <Typography
            type="body"
            className={classNames(
              classes.textContent,
              classes.subTitle,
              classes.propertyAddress
            )}
          >
            {address.label}
          </Typography>
          {avm.properyStats &&
            avm.properyStats.length > 0 && (
              <SellerPageStats
                avm={avm}
                classes={classes}
                renderIcon={renderIcon}
              />
            )}
          {avmType === "foundWithValuation" || avmType === "found" ? (
            <Fragment>
              {avmType === "foundWithValuation" && (
                <SellerPageValuationGraph
                  avm={avm}
                  classes={classes}
                  renderIcon={renderIcon}
                />
              )}
              <Typography
                type="body"
                className={classNames(classes.textContent, classes.subBody)}
              >
                This is an automated valuation based off of tax records, public
                data and recently sold properties. It is not an appraisal. Use
                it as a starting point to determine value. An approved local
                expert may follow up with a more detailed report shortly.
              </Typography>
            </Fragment>
          ) : avmType === "lux" ? (
            <Fragment>
              <Typography
                type="body"
                className={classNames(classes.textContent, classes.subBody)}
              >
                These types of homes are often very unique. And since your
                high-end home is in a special class, when it comes to getting
                it’s value right, it really requires a manual review by a local
                expert that specializes in your type of luxury home.
              </Typography>
            </Fragment>
          ) : (
            // Missing
            <Fragment>
              <Typography
                type="body"
                className={classNames(
                  classes.textContent,
                  classes.subBody,
                  classes.paddingTop
                )}
              >
                Our AVM (automated valuation model) uses many different
                algorithm models to analyze a home’s value. And we only display
                a value if the criteria for an accurate result is fulfilled,
                such as having enough sold data. Also some homes that are very
                rural or unique in other ways are not candidates for an accurate
                automated valuation.
              </Typography>
              <Typography
                type="body"
                className={classNames(classes.textContent, classes.subBody)}
              >
                But no worries! It just means it requires a manual review by a
                local expert that specializes in your type of home.
              </Typography>
              <Typography
                type="body"
                className={classNames(classes.textContent, classes.subBody)}
              >
                So please request a more detailed report from one of our
                approved local experts. There’s no-obligation and we’ll provide
                you with a comprehensive report that will be much more accurate
                than any computer generated report.
              </Typography>
            </Fragment>
          )}
          {!(avmType === "missing") && (
            <Fragment>
              <Typography
                type="body"
                className={classNames(classes.textContent, classes.subBody)}
              >
                So please take the valuation above as just a starting point and
                request a more detailed no-obligation report from one of
                approved local experts.
              </Typography>
              {avmType === "foundWithValuation"
                ? this.renderBlueBox("Is your value incorrect?")
                : this.renderBlueBox("Need More Info?")}
            </Fragment>
          )}
          {this.renderGreenBox()}
        </div>
      </div>
    );
  }
}

const styles = (theme) => {
  const tabletL = 992;
  const tabletP = 768;
  const mobile = 480;

  return {
    boxWrapper: {
      width: "100%",
      [`@media (min-width: ${tabletP}px)`]: {
        width: "80%",
        maxWidth: "1000px",
      },
      display: "flex",
      zIndex: "1",
      position: "relative",
      flexFlow: "column",
      [`@media (min-width: ${mobile}px)`]: {
        marginTop: "calc(3% - 20px)",
      },
      marginBottom: "calc(10%)",
      alignItems: "center",
      justifyContent: "center",
    },
    box: {
      border: `${theme.spacing.unit}px solid white`,
      borderRadius: `${theme.spacing.unit * 1.5}px`,
      backgroundColor: theme.colors.white,
      padding: `${theme.spacing.unit}px`,
      [`@media (min-width: ${tabletL}px)`]: {
        padding: `${theme.spacing.unit * 3}px`,
      },
      textAlign: "center",
      boxShadow: theme.shadows[4],
    },
    textContent: {
      lineHeight: "1.2",
      [`@media (min-width: ${tabletL}px)`]: {
        lineHeight: "1.6",
      },
    },
    subTitle: {
      fontSize: "24px",
    },
    rangePrice: {
      paddingTop: `${theme.spacing.unit}px`,
      paddingBottom: `${theme.spacing.unit}px`,
      textAlign: "center",
    },
    valuationGraphWrapper: { marginBottom: `${theme.spacing.unit}px` },
    estimatesWrapper: {
      display: "flex",
      justifyContent: "space-evenly",
      paddingTop: `${theme.spacing.unit}px`,
      paddingLeft: `${theme.spacing.unit * 2}px`,
      paddingRight: `${theme.spacing.unit * 2}px`,
      cursor: "pointer",
      flexWrap: "wrap",
    },
    estimateWrapper: {
      flexGrow: "1",
      marginBottom: `${theme.spacing.unit}px`,
    },
    propertyStatsWrappers: {
      display: "flex",
      justifyContent: "space-evenly",
      paddingTop: `${theme.spacing.unit}px`,
      paddingBottom: `${theme.spacing.unit}px`,
      flexWrap: "wrap",
    },
    propertyStatsWrapper: {
      flexGrow: "1",
      padding: `${theme.spacing.unit}px`,
      minWidth: "49%",
      [`@media (min-width: ${mobile}px)`]: {
        minWidth: "auto",
      },
    },
    statValue: {
      fontWeight: "bold",
      fontSize: "16px",
    },
    statValueMargin: { marginTop: "14px" },
    statLabel: { fontSize: "12px" },
    statLabelMargin: { marginTop: "4px" },
    stack: {
      display: "inline-block",
      verticalAlign: "middle",
      position: "relative",
      width: "67px",
      height: "66px",
    },
    confidenceScore: {
      position: "absolute",
      left: 0,
      width: "100%",
    },
    confidenceScoreValue: {
      top: "24px",
    },
    confidenceLable: {
      marginTop: "11px",
    },
    boxButton: { bottom: "1px" },
    boxButtonText: { lineHeight: "26px !important" },
    paddingTop: { paddingTop: `${theme.spacing.unit * 1.5}px` },
    subBody: {
      paddingBottom: `${theme.spacing.unit * 1.5}px`,
      fontSize: "20px",
      textAlign: "left",
    },
    marginBottom: { marginBottom: `${theme.spacing.unit}px` },
    contactBox: {
      display: "flex",
      flexWrap: "wrap",
      marginBottom: `${theme.spacing.unit * 2}px`,
      "& $contactTitle, & $contactBody": {
        color: theme.colors.white,
        textAlign: "center",
      },
      padding: `${theme.spacing.unit}px`,
    },

    textFlex: {
      flexGrow: "1",
      [`@media (min-width: ${tabletP}px)`]: {
        maxWidth: "49%",
      },
    },
    contactTitle: { fontSize: "24px", fontWeight: "bold" },
    contactBody: { fontSize: "14px" },
    contactSubText: {
      fontSize: "12px",
      color: theme.colors.white,
    },
    contactButton: {
      margin: `${theme.spacing.unit}px 0`,
    },
    box1Color: {
      backgroundColor: "#69A0DD",
      border: `1px solid #69A0DD`,
      borderRadius: "4px",
      display: "flex",
    },

    box2Color: {
      backgroundColor: "#4EA158",
      border: `1px solid #4EA158`,
      borderRadius: "4px",
      display: "flex",
    },
    iconContainer: {
      margin: "5px 10px 12px",
      display: "inline-block",
      verticalAlign: "middle",
    },
  };
};

export default withGlobalContext(
  withSellerLeadContext(withStyles(styles)(SellerPageThankYou))
);
